import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormattedMessage } from "react-intl";
import { Building2, Shield, Users, BarChart3, Home, Car, Key, Calendar, FileText, MessageSquare } from "lucide-react";
import alproLogo from "@/assets/alpro-logo.png";
import { Link } from "wouter";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <nav className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img src={alproLogo} alt="ALPRO" className="h-8 w-auto mr-3" />
              <span className="text-xl font-bold text-foreground">ALPRO.ai</span>
            </div>
            
            {/* Login Button */}
            <Link href="/login">
              <Button 
                size="lg" 
                className="text-lg px-8 py-2"
                data-testid="button-login"
              >
                <FormattedMessage id="pages.landing.login" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <img 
              src={alproLogo} 
              alt="ALPRO" 
              className="h-24 w-auto mx-auto mb-6"
            />
            <h1 className="text-5xl font-bold text-foreground mb-6">
              <FormattedMessage id="pages.landing.heroTitle" />
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              <FormattedMessage id="pages.landing.heroSubtitle" />
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/login">
                <Button 
                  size="lg" 
                  className="text-lg px-8 py-3 bg-blue-600 hover:bg-blue-700"
                  data-testid="button-get-started"
                >
                  <FormattedMessage id="pages.landing.getStarted" />
                </Button>
              </Link>
              <Button 
                variant="outline"
                size="lg" 
                className="text-lg px-8 py-3"
                data-testid="button-learn-more"
              >
                <FormattedMessage id="pages.landing.learnMore" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              <FormattedMessage id="pages.landing.solutionsTitle" />
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              <FormattedMessage id="pages.landing.solutionsSubtitle" />
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">
                  <FormattedMessage id="pages.landing.features.financial" />
                </h3>
                <p className="text-sm text-muted-foreground">
                  <FormattedMessage id="pages.landing.features.financialDesc" />
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">
                  <FormattedMessage id="pages.landing.features.resident" />
                </h3>
                <p className="text-sm text-muted-foreground">
                  <FormattedMessage id="pages.landing.features.residentDesc" />
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">
                  <FormattedMessage id="pages.landing.features.operations" />
                </h3>
                <p className="text-sm text-muted-foreground">
                  <FormattedMessage id="pages.landing.features.operationsDesc" />
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">
                  <FormattedMessage id="pages.landing.features.documents" />
                </h3>
                <p className="text-sm text-muted-foreground">
                  <FormattedMessage id="pages.landing.features.documentsDesc" />
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Resident Services */}
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-8">
              <FormattedMessage id="pages.landing.residentServicesTitle" />
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6 text-center">
                  <FileText className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-foreground mb-2"><FormattedMessage id="pages.landing.services.invoices" /></h4>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage id="pages.landing.services.invoicesDesc" />
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <Car className="w-10 h-10 text-green-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-foreground mb-2"><FormattedMessage id="pages.landing.services.cars" /></h4>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage id="pages.landing.services.carsDesc" />
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <Key className="w-10 h-10 text-purple-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-foreground mb-2"><FormattedMessage id="pages.landing.services.accessKeys" /></h4>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage id="pages.landing.services.accessKeysDesc" />
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <Calendar className="w-10 h-10 text-orange-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-foreground mb-2"><FormattedMessage id="pages.landing.services.amenities" /></h4>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage id="pages.landing.services.amenitiesDesc" />
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <MessageSquare className="w-10 h-10 text-red-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-foreground mb-2"><FormattedMessage id="pages.landing.services.announcements" /></h4>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage id="pages.landing.services.announcementsDesc" />
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <Home className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-foreground mb-2"><FormattedMessage id="pages.landing.services.workOrders" /></h4>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage id="pages.landing.services.workOrdersDesc" />
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* User Roles */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-foreground mb-8">
              <FormattedMessage id="pages.landing.rolesTitle" />
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <Card>
                <CardContent className="p-6 text-center">
                  <h4 className="font-semibold text-foreground mb-2"><FormattedMessage id="pages.landing.roles.managers" /></h4>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage id="pages.landing.roles.managersDesc" />
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <h4 className="font-semibold text-foreground mb-2"><FormattedMessage id="pages.landing.roles.owners" /></h4>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage id="pages.landing.roles.ownersDesc" />
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <h4 className="font-semibold text-foreground mb-2"><FormattedMessage id="pages.landing.roles.renters" /></h4>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage id="pages.landing.roles.rentersDesc" />
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-blue-600 dark:bg-blue-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            <FormattedMessage id="pages.landing.ctaTitle" />
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            <FormattedMessage id="pages.landing.ctaSubtitle" />
          </p>
          <Link href="/login">
            <Button 
              size="lg" 
              variant="outline"
              className="text-lg px-8 py-3 bg-card text-primary hover:bg-muted"
              data-testid="button-cta-login"
            >
              <FormattedMessage id="pages.landing.loginToPortal" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <img src={alproLogo} alt="ALPRO" className="h-8 w-auto mr-3" />
            <span className="text-xl font-bold text-white">ALPRO.ai</span>
          </div>
          <p className="text-gray-400">
            <FormattedMessage id="pages.landing.copyright" />
          </p>
        </div>
      </footer>
    </div>
  );
}